import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  LinearProgress,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Slide,
  Card,
  CardContent,
  useMediaQuery,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  School as SchoolIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  AutoAwesome as AIIcon,
  Psychology as BrainIcon,
  DragIndicator as DragIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  Fullscreen as MaximizeIcon,
  FullscreenExit as MinimizeIcon,
  ViewSidebar as SidebarIcon,
  ViewColumn as BottomIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';

interface FillGapsExerciseModalProps {
  open: boolean;
  onClose: () => void;
  documentContent: string;
}

interface ExerciseQuestion {
  id: string;
  text: string;
  gaps: Array<{
    id: string;
    placeholder: string;
    correctAnswer: string;
    userAnswer: string;
    isCorrect: boolean | null;
    isFilled: boolean;
  }>;
  answers: Array<{
    id: string;
    text: string;
    isUsed: boolean;
  }>;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
}

const FillGapsExerciseModal: React.FC<FillGapsExerciseModalProps> = ({
  open,
  onClose,
  documentContent,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [currentExercise, setCurrentExercise] = useState<ExerciseQuestion | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [completedExercises, setCompletedExercises] = useState<ExerciseQuestion[]>([]);
  const [isMaximized, setIsMaximized] = useState(false);
  const [layoutMode, setLayoutMode] = useState<'bottom' | 'sidebar'>('bottom');
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(null);
  const [selectedGapId, setSelectedGapId] = useState<string | null>(null);

  // Sample exercise data
  const sampleExercises: ExerciseQuestion[] = [
    {
      id: 'sample-1',
      text: 'Richard Sanchez is a **Marketing Manager** with extensive experience in **strategic marketing**, **team leadership**, and **brand management**. He holds both a **Master of Business Management** and **Bachelor of Business** degree from **Wardiere University**, where he graduated with an impressive **3.8 GPA**.',
      gaps: [
        { id: 'gap-1', placeholder: 'Marketing Manager', correctAnswer: 'Marketing Manager', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-2', placeholder: 'strategic marketing', correctAnswer: 'strategic marketing', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-3', placeholder: 'team leadership', correctAnswer: 'team leadership', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-4', placeholder: 'brand management', correctAnswer: 'brand management', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-5', placeholder: 'Master of Business Management', correctAnswer: 'Master of Business Management', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-6', placeholder: 'Bachelor of Business', correctAnswer: 'Bachelor of Business', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-7', placeholder: 'Wardiere University', correctAnswer: 'Wardiere University', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-8', placeholder: '3.8 GPA', correctAnswer: '3.8 GPA', userAnswer: '', isCorrect: null, isFilled: false },
      ],
      answers: [
        { id: 'ans-1', text: 'Marketing Manager', isUsed: false },
        { id: 'ans-2', text: 'strategic marketing', isUsed: false },
        { id: 'ans-3', text: 'team leadership', isUsed: false },
        { id: 'ans-4', text: 'brand management', isUsed: false },
        { id: 'ans-5', text: 'Master of Business Management', isUsed: false },
        { id: 'ans-6', text: 'Bachelor of Business', isUsed: false },
        { id: 'ans-7', text: 'Wardiere University', isUsed: false },
        { id: 'ans-8', text: '3.8 GPA', isUsed: false },
        { id: 'ans-9', text: 'Project Manager', isUsed: false },
        { id: 'ans-10', text: 'Harvard University', isUsed: false },
        { id: 'ans-11', text: '4.0 GPA', isUsed: false },
        { id: 'ans-12', text: 'digital marketing', isUsed: false },
      ],
      difficulty: 'medium',
      category: 'Professional Profile',
    },
    {
      id: 'sample-2',
      text: 'His professional experience spans multiple roles as a **Marketing Manager & Specialist** across three prominent studios—**Borcelle Studio**, **Fauget Studio**, and **Studio Shodwe**—where he consistently developed and executed **high-impact marketing strategies**, managed **budgets** to optimize **return on investment**, and maintained rigorous **brand consistency** across all channels.',
      gaps: [
        { id: 'gap-1', placeholder: 'Marketing Manager & Specialist', correctAnswer: 'Marketing Manager & Specialist', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-2', placeholder: 'Borcelle Studio', correctAnswer: 'Borcelle Studio', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-3', placeholder: 'Fauget Studio', correctAnswer: 'Fauget Studio', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-4', placeholder: 'Studio Shodwe', correctAnswer: 'Studio Shodwe', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-5', placeholder: 'high-impact marketing strategies', correctAnswer: 'high-impact marketing strategies', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-6', placeholder: 'budgets', correctAnswer: 'budgets', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-7', placeholder: 'return on investment', correctAnswer: 'return on investment', userAnswer: '', isCorrect: null, isFilled: false },
        { id: 'gap-8', placeholder: 'brand consistency', correctAnswer: 'brand consistency', userAnswer: '', isCorrect: null, isFilled: false },
      ],
      answers: [
        { id: 'ans-1', text: 'Marketing Manager & Specialist', isUsed: false },
        { id: 'ans-2', text: 'Borcelle Studio', isUsed: false },
        { id: 'ans-3', text: 'Fauget Studio', isUsed: false },
        { id: 'ans-4', text: 'Studio Shodwe', isUsed: false },
        { id: 'ans-5', text: 'high-impact marketing strategies', isUsed: false },
        { id: 'ans-6', text: 'budgets', isUsed: false },
        { id: 'ans-7', text: 'return on investment', isUsed: false },
        { id: 'ans-8', text: 'brand consistency', isUsed: false },
        { id: 'ans-9', text: 'Creative Director', isUsed: false },
        { id: 'ans-10', text: 'Google Studio', isUsed: false },
        { id: 'ans-11', text: 'social media marketing', isUsed: false },
        { id: 'ans-12', text: 'customer satisfaction', isUsed: false },
      ],
      difficulty: 'hard',
      category: 'Work Experience',
    },
  ];

  useEffect(() => {
    if (open && !currentExercise) {
      // Load first sample exercise
      setCurrentExercise(sampleExercises[0]);
      setTotalQuestions(sampleExercises[0].gaps.length);
    }
  }, [open, currentExercise]);

  const generateNewExercise = async () => {
    setIsGenerating(true);
    
    // Simulate AI generation delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // For now, cycle through sample exercises
    const currentIndex = sampleExercises.findIndex(ex => ex.id === currentExercise?.id);
    const nextIndex = (currentIndex + 1) % sampleExercises.length;
    const newExercise = { ...sampleExercises[nextIndex] };
    
    // Reset user answers and answers
    newExercise.gaps = newExercise.gaps.map(gap => ({
      ...gap,
      userAnswer: '',
      isCorrect: null,
      isFilled: false,
    }));
    
    newExercise.answers = newExercise.answers.map(answer => ({
      ...answer,
      isUsed: false,
    }));
    
    setCurrentExercise(newExercise);
    setTotalQuestions(newExercise.gaps.length);
    setShowResults(false);
    setScore(0);
    setIsGenerating(false);
  };

  const handleDragStart = (answerId: string) => {
    // Visual feedback for drag start
    console.log('Drag started:', answerId);
  };

  const handleDragEnd = (answerId: string, gapId: string) => {
    if (!currentExercise) return;
    
    const answer = currentExercise.answers.find(a => a.id === answerId);
    const gap = currentExercise.gaps.find(g => g.id === gapId);
    
    if (!answer || !gap || answer.isUsed || gap.isFilled) return;
    
    // Check if answer is correct
    const isCorrect = answer.text.toLowerCase().trim() === gap.correctAnswer.toLowerCase().trim();
    
    // Update exercise state
    const updatedExercise = {
      ...currentExercise,
      gaps: currentExercise.gaps.map(g =>
        g.id === gapId 
          ? { ...g, userAnswer: answer.text, isCorrect, isFilled: true }
          : g
      ),
      answers: currentExercise.answers.map(a =>
        a.id === answerId 
          ? { ...a, isUsed: true }
          : a
      ),
    };
    
    setCurrentExercise(updatedExercise);
  };

  const removeAnswerFromGap = (gapId: string) => {
    if (!currentExercise) return;
    
    const gap = currentExercise.gaps.find(g => g.id === gapId);
    if (!gap || !gap.isFilled) return;
    
    // Find the answer that was used for this gap
    const answer = currentExercise.answers.find(a => a.text === gap.userAnswer);
    
    const updatedExercise = {
      ...currentExercise,
      gaps: currentExercise.gaps.map(g =>
        g.id === gapId 
          ? { ...g, userAnswer: '', isCorrect: null, isFilled: false }
          : g
      ),
      answers: currentExercise.answers.map(a =>
        a.id === answer?.id 
          ? { ...a, isUsed: false }
          : a
      ),
    };
    
    setCurrentExercise(updatedExercise);
  };

  const handleGapClick = (gapId: string, event: React.MouseEvent<HTMLElement>) => {
    if (!currentExercise) return;
    
    const gap = currentExercise.gaps.find(g => g.id === gapId);
    if (!gap || gap.isFilled || showResults) return;
    
    setSelectedGapId(gapId);
    setDropdownAnchor(event.currentTarget);
  };

  const handleAnswerSelect = (answerId: string) => {
    if (!currentExercise || !selectedGapId) return;
    
    const answer = currentExercise.answers.find(a => a.id === answerId);
    const gap = currentExercise.gaps.find(g => g.id === selectedGapId);
    
    if (!answer || !gap || answer.isUsed) return;
    
    // Check if answer is correct
    const isCorrect = answer.text.toLowerCase().trim() === gap.correctAnswer.toLowerCase().trim();
    
    // Update exercise state
    const updatedExercise = {
      ...currentExercise,
      gaps: currentExercise.gaps.map(g =>
        g.id === selectedGapId 
          ? { ...g, userAnswer: answer.text, isCorrect, isFilled: true }
          : g
      ),
      answers: currentExercise.answers.map(a =>
        a.id === answerId 
          ? { ...a, isUsed: true }
          : a
      ),
    };
    
    setCurrentExercise(updatedExercise);
    setDropdownAnchor(null);
    setSelectedGapId(null);
  };

  const handleDropdownClose = () => {
    setDropdownAnchor(null);
    setSelectedGapId(null);
  };

  const handleAnswerChange = (gapId: string, value: string) => {
    if (!currentExercise) return;
    
    const updatedExercise = {
      ...currentExercise,
      gaps: currentExercise.gaps.map(gap =>
        gap.id === gapId ? { ...gap, userAnswer: value } : gap
      ),
    };
    setCurrentExercise(updatedExercise);
  };

  const checkAnswers = () => {
    if (!currentExercise) return;
    
    let correctCount = 0;
    const updatedExercise = {
      ...currentExercise,
      gaps: currentExercise.gaps.map(gap => {
        const isCorrect = gap.userAnswer.toLowerCase().trim() === gap.correctAnswer.toLowerCase().trim();
        if (isCorrect) correctCount++;
        return { ...gap, isCorrect };
      }),
    };
    
    setCurrentExercise(updatedExercise);
    setScore(correctCount);
    setShowResults(true);
    setCompletedExercises(prev => [...prev, updatedExercise]);
  };

  const resetExercise = () => {
    if (!currentExercise) return;
    
    const resetExercise = {
      ...currentExercise,
      gaps: currentExercise.gaps.map(gap => ({
        ...gap,
        userAnswer: '',
        isCorrect: null,
        isFilled: false,
      })),
      answers: currentExercise.answers.map(answer => ({
        ...answer,
        isUsed: false,
      })),
    };
    setCurrentExercise(resetExercise);
    setShowResults(false);
    setScore(0);
  };

  const renderExerciseText = () => {
    if (!currentExercise) return null;
    
    let text = currentExercise.text;
    let gapIndex = 0;
    
    // Replace **text** with drag-and-drop zones
    const parts = text.split(/(\*\*.*?\*\*)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const gapText = part.slice(2, -2);
        const gap = currentExercise.gaps[gapIndex];
        gapIndex++;
        
        return (
          <div
            key={`gap-${gap.id}`}
            className="drop-zone"
            onDrop={(e: React.DragEvent) => {
              e.preventDefault();
              const answerId = e.dataTransfer.getData('text/plain');
              handleDragEnd(answerId, gap.id);
            }}
            onDragOver={(e: React.DragEvent) => e.preventDefault()}
            onDragEnter={(e: React.DragEvent) => e.preventDefault()}
            style={{
              display: 'inline-block',
              margin: isMobile ? '0 2px' : '0 4px',
              minWidth: isMobile 
                ? Math.max(gap.placeholder.length * 6, 80)
                : Math.max(gap.placeholder.length * 8, 120),
              minHeight: isMobile ? 32 : 40,
              borderRadius: isMobile ? 16 : 20,
              border: gap.isFilled 
                ? showResults
                  ? gap.isCorrect
                    ? '2px solid #10b981'
                    : '2px solid #ef4444'
                  : '2px solid #3b82f6'
                : '2px dashed #cbd5e1',
              backgroundColor: gap.isFilled 
                ? showResults
                  ? gap.isCorrect
                    ? alpha('#10b981', 0.1)
                    : alpha('#ef4444', 0.1)
                  : alpha('#3b82f6', 0.1)
                : alpha('#f8fafc', 0.5),
              padding: isMobile ? '6px 12px' : '8px 16px',
              cursor: gap.isFilled ? 'pointer' : (showResults ? 'default' : 'pointer'),
              position: 'relative',
              transition: 'all 0.3s ease',
              touchAction: 'none', // Prevent scrolling on touch
            }}
            onClick={(e) => {
              if (gap.isFilled && !showResults) {
                removeAnswerFromGap(gap.id);
              } else if (!gap.isFilled && !showResults) {
                handleGapClick(gap.id, e);
              }
            }}
          >
            {gap.isFilled ? (
              <motion.div
                whileHover={{ scale: 1.05 }}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 8,
                  minHeight: 24,
                }}
              >
                <Typography sx={{ 
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  fontWeight: 600,
                  color: showResults
                    ? gap.isCorrect
                      ? '#10b981'
                      : '#ef4444'
                    : '#1f2937',
                  textAlign: 'center',
                }}>
                  {gap.userAnswer}
                </Typography>
                {!showResults && (
                  <IconButton
                    size="small"
                    sx={{ 
                      p: 0.5,
                      color: '#6b7280',
                      '&:hover': { color: '#ef4444' }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </motion.div>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: 24,
                position: 'relative',
              }}>
                <Typography sx={{ 
                  fontSize: isMobile ? '0.7rem' : '0.8rem',
                  color: '#9ca3af',
                  fontStyle: 'italic',
                  textAlign: 'center',
                }}>
                  {gap.placeholder}
                </Typography>
                {!showResults && (
                  <Box sx={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                    opacity: 0.7,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 0.7, transform: 'scale(1)' },
                      '50%': { opacity: 1, transform: 'scale(1.2)' },
                      '100%': { opacity: 0.7, transform: 'scale(1)' },
                    },
                  }} />
                )}
              </Box>
            )}
          </div>
        );
      }
      return (
        <Typography
          key={`text-${index}`}
          component="span"
          sx={{
            fontSize: isMobile ? '0.9rem' : '1.1rem',
            lineHeight: isMobile ? 1.6 : 1.8,
            color: '#374151',
            fontWeight: 400,
          }}
        >
          {part}
        </Typography>
      );
    });
  };

  const renderAnswerBank = () => {
    if (!currentExercise) return null;
    
    const availableAnswers = currentExercise.answers.filter(answer => !answer.isUsed);
    
    return (
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile 
          ? 'repeat(auto-fit, minmax(100px, 1fr))'
          : layoutMode === 'sidebar' 
            ? 'repeat(auto-fit, minmax(120px, 1fr))' 
            : 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: isMobile ? 1 : (layoutMode === 'sidebar' ? 1.5 : 2),
        mt: (layoutMode === 'sidebar' && !isMobile) ? 0 : (isMobile ? 0 : 3),
      }}>
        {availableAnswers.map((answer) => (
          <div
            key={answer.id}
            draggable
            onDragStart={(e: React.DragEvent) => {
              e.dataTransfer.setData('text/plain', answer.id);
              handleDragStart(answer.id);
            }}
            style={{
              cursor: 'grab',
            }}
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Card
                sx={{
                  p: isMobile ? 1 : (layoutMode === 'sidebar' ? 1.5 : 2),
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                  border: '2px solid transparent',
                  backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'content-box, border-box',
                  boxShadow: '0 4px 20px rgba(139, 92, 246, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(139, 92, 246, 0.2)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  gap: 1,
                }}>
                  <Typography sx={{ 
                    fontSize: isMobile ? '0.7rem' : (layoutMode === 'sidebar' ? '0.8rem' : '0.9rem'),
                    fontWeight: 600,
                    color: '#1f2937',
                    flex: 1,
                    textAlign: 'center',
                    lineHeight: isMobile ? 1.1 : (layoutMode === 'sidebar' ? 1.2 : 1.4),
                  }}>
                    {answer.text}
                  </Typography>
                  <DragIcon sx={{ 
                    fontSize: isMobile ? 14 : (layoutMode === 'sidebar' ? 16 : 20), 
                    color: '#8b5cf6',
                    opacity: 0.7,
                  }} />
                </Box>
              </Card>
            </motion.div>
          </div>
        ))}
      </Box>
    );
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'hard': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '🟢';
      case 'medium': return '🟡';
      case 'hard': return '🔴';
      default: return '⚪';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMaximized ? false : (isMobile ? "sm" : "lg")}
      fullWidth={isMaximized || isMobile}
      fullScreen={isMaximized || isMobile}
      PaperProps={{
        sx: {
          borderRadius: (isMaximized || isMobile) ? 0 : 4,
          background: 'white',
          border: (isMaximized || isMobile) ? 'none' : '3px solid transparent',
          backgroundImage: (isMaximized || isMobile) ? 'none' : 'linear-gradient(white, white), linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'content-box, border-box',
          boxShadow: (isMaximized || isMobile) ? 'none' : '0 20px 60px rgba(236, 72, 153, 0.3), 0 8px 25px rgba(139, 92, 246, 0.2)',
          overflow: 'hidden',
          minHeight: (isMaximized || isMobile) ? '100vh' : '600px',
          maxHeight: (isMaximized || isMobile) ? '100vh' : '90vh',
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        py: isMobile ? 1.5 : 2,
        px: isMobile ? 2 : 4,
        background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
        color: 'white',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: isMobile ? 1 : 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          pointerEvents: 'none',
        }
      }}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease',
            position: 'relative',
            zIndex: 2,
            minWidth: isMobile ? 40 : 48,
            minHeight: isMobile ? 40 : 48,
          }}
        >
          <CloseIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
        </IconButton>

        {/* Center Content */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: isMobile ? 1 : 2,
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minWidth: 0, // Allow text to wrap
        }}>
          <Box sx={{
            width: isMobile ? 32 : 40,
            height: isMobile ? 32 : 40,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            flexShrink: 0,
          }}>
            <SchoolIcon sx={{ fontSize: isMobile ? 18 : 22, color: 'white' }} />
          </Box>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ 
              fontWeight: 700, 
              fontSize: isMobile ? '1.1rem' : '1.5rem',
              textAlign: isMobile ? 'center' : 'left',
              lineHeight: 1.2,
            }}
          >
            {isMobile ? 'Fill in the Blanks' : 'Fill in the Blanks Exercise'}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: isMobile ? 0.5 : 1,
          flexShrink: 0,
        }}>
          {/* Layout Toggle Button - Hide on mobile since sidebar doesn't work well */}
          {!isMobile && (
            <IconButton
              onClick={() => setLayoutMode(layoutMode === 'bottom' ? 'sidebar' : 'bottom')}
              sx={{
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
                position: 'relative',
                zIndex: 2,
                minWidth: isMobile ? 36 : 40,
                minHeight: isMobile ? 36 : 40,
              }}
              title={layoutMode === 'bottom' ? 'Switch to Sidebar Layout' : 'Switch to Bottom Layout'}
            >
              {layoutMode === 'bottom' ? <SidebarIcon sx={{ fontSize: isMobile ? 18 : 20 }} /> : <BottomIcon sx={{ fontSize: isMobile ? 18 : 20 }} />}
            </IconButton>
          )}

          {/* Maximize/Minimize Button - Hide on mobile since it's already fullscreen */}
          {!isMobile && (
            <IconButton
              onClick={() => setIsMaximized(!isMaximized)}
              sx={{
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
                position: 'relative',
                zIndex: 2,
                minWidth: isMobile ? 36 : 40,
                minHeight: isMobile ? 36 : 40,
              }}
              title={isMaximized ? 'Minimize' : 'Maximize'}
            >
              {isMaximized ? <MinimizeIcon sx={{ fontSize: isMobile ? 18 : 20 }} /> : <MaximizeIcon sx={{ fontSize: isMobile ? 18 : 20 }} />}
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ 
        px: isMobile ? 2 : 4, 
        py: isMobile ? 2 : 4,
        background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
        position: 'relative',
        display: 'flex',
        flexDirection: (layoutMode === 'sidebar' && !isMobile) ? 'row' : 'column',
        gap: (layoutMode === 'sidebar' && !isMobile) ? 4 : 0,
        overflow: isMobile ? 'auto' : 'visible',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(236, 72, 153, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}>
        {currentExercise && (
          <Box sx={{ 
            position: 'relative', 
            zIndex: 1,
            display: 'flex',
            flexDirection: layoutMode === 'sidebar' ? 'column' : 'column',
            flex: layoutMode === 'sidebar' ? 1 : 'none',
            minWidth: layoutMode === 'sidebar' ? 0 : 'auto',
          }}>
            {/* Exercise Info */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isMobile ? 1 : 2, 
              mb: isMobile ? 3 : 4,
              flexWrap: isMobile ? 'wrap' : 'nowrap',
            }}>
              <Chip
                label={`${getDifficultyIcon(currentExercise.difficulty)} ${currentExercise.difficulty.toUpperCase()}`}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  backgroundColor: alpha(getDifficultyColor(currentExercise.difficulty), 0.1),
                  color: getDifficultyColor(currentExercise.difficulty),
                  fontWeight: 600,
                  border: `1px solid ${alpha(getDifficultyColor(currentExercise.difficulty), 0.3)}`,
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                }}
              />
              <Chip
                label={currentExercise.category}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  backgroundColor: alpha('#3b82f6', 0.1),
                  color: '#3b82f6',
                  fontWeight: 600,
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                }}
              />
              <Chip
                label={`${currentExercise.gaps.length} Gaps`}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  backgroundColor: alpha('#8b5cf6', 0.1),
                  color: '#8b5cf6',
                  fontWeight: 600,
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                }}
              />
            </Box>

            {/* Exercise Text */}
            <Paper
              elevation={2}
              sx={{
                p: isMobile ? 2 : 4,
                mb: isMobile ? 3 : 4,
                borderRadius: 3,
                background: 'white',
                border: '2px solid rgba(236, 72, 153, 0.1)',
                minHeight: isMobile ? '150px' : '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ 
                textAlign: 'center', 
                width: '100%',
                fontSize: isMobile ? '0.9rem' : '1.1rem',
                lineHeight: isMobile ? 1.6 : 1.8,
              }}>
                {renderExerciseText()}
              </Box>
            </Paper>

            {/* Answer Bank - Bottom Layout */}
            {(layoutMode === 'bottom' || isMobile) && (
              <Paper
                elevation={2}
                sx={{
                  p: isMobile ? 2 : 3,
                  mb: isMobile ? 3 : 4,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                  border: '2px solid rgba(139, 92, 246, 0.1)',
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: isMobile ? 1 : 2, 
                  mb: isMobile ? 2 : 3,
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                }}>
                  <DragIcon sx={{ fontSize: isMobile ? 20 : 24, color: '#8b5cf6' }} />
                  <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ 
                    fontWeight: 700, 
                    color: '#1f2937',
                    fontSize: isMobile ? '1rem' : '1.1rem'
                  }}>
                    {isMobile ? 'Answers' : 'Drag & Drop Answers'}
                  </Typography>
                  <Chip
                    label={`${currentExercise.answers.filter(a => !a.isUsed).length} Available`}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      backgroundColor: alpha('#8b5cf6', 0.1),
                      color: '#8b5cf6',
                      fontWeight: 600,
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                    }}
                  />
                </Box>
                {renderAnswerBank()}
              </Paper>
            )}

            {/* Results */}
            {showResults && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: score === totalQuestions 
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(34, 197, 94, 0.1) 100%)'
                      : score > totalQuestions / 2
                      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(248, 113, 113, 0.1) 100%)',
                    border: `2px solid ${score === totalQuestions 
                      ? 'rgba(16, 185, 129, 0.3)'
                      : score > totalQuestions / 2
                      ? 'rgba(245, 158, 11, 0.3)'
                      : 'rgba(239, 68, 68, 0.3)'}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                {score === totalQuestions ? (
                  <CheckIcon sx={{ fontSize: 32, color: '#10b981' }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 32, color: score > totalQuestions / 2 ? '#f59e0b' : '#ef4444' }} />
                )}
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: score === totalQuestions 
                    ? '#10b981'
                    : score > totalQuestions / 2
                    ? '#f59e0b'
                    : '#ef4444'
                }}>
                  {score}/{totalQuestions}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ 
                textAlign: 'center', 
                fontWeight: 600,
                color: score === totalQuestions 
                  ? '#10b981'
                  : score > totalQuestions / 2
                  ? '#f59e0b'
                  : '#ef4444'
              }}>
                {score === totalQuestions 
                  ? 'Perfect! 🎉' 
                  : score > totalQuestions / 2
                  ? 'Good Job! 👍'
                  : 'Keep Trying! 💪'}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: '#6b7280' }}>
                {Math.round((score / totalQuestions) * 100)}% Correct
              </Typography>
            </Paper>
          </motion.div>
        )}

        {/* Progress Bar */}
        {!showResults && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Progress
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {currentExercise.gaps.filter(gap => gap.isFilled).length} / {totalQuestions}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(currentExercise.gaps.filter(gap => gap.isFilled).length / totalQuestions) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha('#e2e8f0', 0.3),
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        )}
      </Box>
    )}

    {/* Answer Bank - Sidebar Layout */}
    {currentExercise && layoutMode === 'sidebar' && (
      <Box sx={{ 
        position: 'relative', 
        zIndex: 1,
        minWidth: 300,
        maxWidth: 400,
        flexShrink: 0,
      }}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            border: '2px solid rgba(139, 92, 246, 0.1)',
            height: 'fit-content',
            position: 'sticky',
            top: 20,
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 3 
          }}>
            <DragIcon sx={{ fontSize: 24, color: '#8b5cf6' }} />
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              color: '#1f2937',
              fontSize: '1.1rem'
            }}>
              Drag & Drop Answers
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Chip
              label={`${currentExercise.answers.filter(a => !a.isUsed).length} Available`}
              sx={{
                backgroundColor: alpha('#8b5cf6', 0.1),
                color: '#8b5cf6',
                fontWeight: 600,
                border: '1px solid rgba(139, 92, 246, 0.3)',
              }}
            />
          </Box>
          {renderAnswerBank()}
        </Paper>
      </Box>
    )}
  </DialogContent>

  {/* Beautiful Answer Dropdown */}
  <Popover
    open={Boolean(dropdownAnchor)}
    anchorEl={dropdownAnchor}
    onClose={handleDropdownClose}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
    PaperProps={{
      sx: {
        borderRadius: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '2px solid transparent',
        backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'content-box, border-box',
        boxShadow: '0 20px 60px rgba(139, 92, 246, 0.3), 0 8px 25px rgba(139, 92, 246, 0.2)',
        overflow: 'hidden',
        maxHeight: isMobile ? '60vh' : '70vh',
        minWidth: isMobile ? 280 : 320,
        maxWidth: isMobile ? '90vw' : 400,
      }
    }}
  >
    <Box sx={{ p: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        mb: 2,
        pb: 2,
        borderBottom: '1px solid rgba(139, 92, 246, 0.1)',
      }}>
        <DragIcon sx={{ fontSize: 24, color: '#8b5cf6' }} />
        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          color: '#1f2937',
          fontSize: isMobile ? '1rem' : '1.1rem'
        }}>
          Choose Answer
        </Typography>
        <Chip
          label={`${currentExercise?.answers.filter(a => !a.isUsed).length || 0} Available`}
          size="small"
          sx={{
            backgroundColor: alpha('#8b5cf6', 0.1),
            color: '#8b5cf6',
            fontWeight: 600,
            border: '1px solid rgba(139, 92, 246, 0.3)',
            fontSize: '0.75rem',
          }}
        />
      </Box>
      
      <List sx={{ p: 0, maxHeight: isMobile ? '50vh' : '60vh', overflow: 'auto' }}>
        {currentExercise?.answers
          .filter(answer => !answer.isUsed)
          .map((answer, index) => (
            <motion.div
              key={answer.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleAnswerSelect(answer.id)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                    border: '1px solid rgba(139, 92, 246, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(139, 92, 246, 0.1) 100%)',
                      borderColor: 'rgba(139, 92, 246, 0.3)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 20px rgba(139, 92, 246, 0.15)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}>
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={answer.text}
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 600,
                        color: '#1f2937',
                        fontSize: isMobile ? '0.875rem' : '0.9rem',
                        lineHeight: 1.4,
                      }
                    }}
                  />
                  <DragIcon sx={{ 
                    fontSize: 20, 
                    color: '#8b5cf6',
                    opacity: 0.7,
                  }} />
                </ListItemButton>
              </ListItem>
            </motion.div>
          ))}
      </List>
      
      {currentExercise?.answers.filter(a => !a.isUsed).length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          color: '#6b7280',
        }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            No more answers available
          </Typography>
        </Box>
      )}
    </Box>
  </Popover>

  {/* Actions */}
  <DialogActions sx={{ 
    p: isMobile ? 2 : 4, 
    pt: isMobile ? 1 : 2,
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderTop: '1px solid rgba(236, 72, 153, 0.1)',
    position: 'relative',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? 1 : 0,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent 0%, rgba(236, 72, 153, 0.3) 50%, transparent 100%)',
    }
  }}>
    <Box sx={{ 
      display: 'flex', 
      gap: isMobile ? 1 : 2, 
      width: '100%', 
      justifyContent: isMobile ? 'center' : 'space-between',
      flexDirection: isMobile ? 'column' : 'row',
    }}>
      <Box sx={{ 
        display: 'flex', 
        gap: isMobile ? 1 : 2,
        flexDirection: isMobile ? 'row' : 'row',
        justifyContent: isMobile ? 'center' : 'flex-start',
      }}>
        <Button
          onClick={resetExercise}
          variant="outlined"
          disabled={!currentExercise || isGenerating}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            px: isMobile ? 2 : 3,
            py: isMobile ? 1 : 1.5,
            borderColor: '#e2e8f0',
            color: '#64748b',
            fontSize: isMobile ? '0.875rem' : '1rem',
            '&:hover': {
              borderColor: '#cbd5e1',
              backgroundColor: '#f8fafc',
            },
          }}
        >
          Reset
        </Button>
        
        <Button
          onClick={generateNewExercise}
          variant="contained"
          disabled={isGenerating}
          size={isMobile ? 'small' : 'medium'}
          startIcon={isGenerating ? <CircularProgress size={isMobile ? 16 : 20} /> : <AIIcon />}
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            px: isMobile ? 2 : 3,
            py: isMobile ? 1 : 1.5,
            fontSize: isMobile ? '0.875rem' : '1rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)',
            },
            '&:disabled': {
              backgroundColor: '#e2e8f0',
              color: '#94a3b8',
            },
          }}
        >
          {isGenerating ? (isMobile ? 'Generating...' : 'Generating...') : (isMobile ? 'New Exercise' : 'Generate New Exercise')}
        </Button>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        gap: isMobile ? 1 : 2,
        justifyContent: isMobile ? 'center' : 'flex-end',
      }}>
        {!showResults ? (
          <Button
            onClick={checkAnswers}
            variant="contained"
            disabled={!currentExercise || currentExercise.gaps.some(gap => !gap.isFilled)}
            size={isMobile ? 'small' : 'medium'}
            startIcon={<CheckIcon />}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              px: isMobile ? 3 : 4,
              py: isMobile ? 1 : 1.5,
              fontSize: isMobile ? '0.875rem' : '1rem',
              background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              },
              '&:disabled': {
                backgroundColor: '#e2e8f0',
                color: '#94a3b8',
              },
            }}
          >
            {isMobile ? 'Check' : 'Check Answers'}
          </Button>
        ) : (
          <Button
            onClick={onClose}
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              px: isMobile ? 3 : 4,
              py: isMobile ? 1 : 1.5,
              fontSize: isMobile ? '0.875rem' : '1rem',
              background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              },
            }}
          >
            Close
          </Button>
        )}
      </Box>
    </Box>
  </DialogActions>
</Dialog>
);
};

export default FillGapsExerciseModal;
